<template>
  <sc-page-base title="Certificado digital">
    <h2 class="subtitle">
      <span>Adicione seus certificados digitais de acordo com o centro de distribuição, assim a gente consegue buscar os CT-es de maneira segura e ágil direto na SEFAZ.</span>
    </h2>
    <section>
      <sc-table
        :loading="loadingWarehouses" :data="warehouses" :paginated="isPaginated" :perPage.sync="perPage"
        :bordered="false" :hoverable="false" :striped="false" :scrollable="false">
        <template slot-scope="{ row, index }">
          <sc-table-column label="Centro de distribuição" :centered="false">
            {{ row.warehouse_name }}
          </sc-table-column>
          <sc-table-column label="Código" :centered="false">
            {{ row.warehouse_code }}
          </sc-table-column>
          <sc-table-column label="CEP" :centered="false">
            {{ row.warehouse_zipcode | cep }}
          </sc-table-column>
          <sc-table-column label="CNPJ" :centered="false">
            <span v-if="row.warehouse_federal_tax_id">
              {{ row.warehouse_federal_tax_id | cnpj }}
            </span>
            <span v-else> - </span>
          </sc-table-column>
          <sc-table-column label="Status" :centered="false">
            <sc-tag size="is-medium">
              <span v-if="row.configuration_state === 'CONFIGURED'">
                <i class="fas fa-circle configured-icon"></i>
                Adicionado - {{ row.configuration_date | utcDate('DD/MM/YY - HH:mm') }}
              </span>
              <span v-else>Nenhum certificado adicionado</span>
            </sc-tag>
          </sc-table-column>
          <sc-table-column label="Ações">
            <sc-dropdown :position="getActionMenuPosition(index)">
              <div class="field" slot="trigger">
                <i class="fas fa-ellipsis-v"></i>
              </div>
              <span v-if="row.configuration_state === 'CONFIGURED'">
                <sc-dropdown-item @click="openAddCertificateModal(row, true)">
                  <i class="far fa-times-circle"></i> Atualizar certificado
                </sc-dropdown-item>
                <sc-dropdown-item @click="openExcludeCertificateModal(row)">
                  <i class="far fa-times-circle"></i> Excluir certificado
                </sc-dropdown-item>
              </span>
              <sc-dropdown-item v-else @click="openAddCertificateModal(row, false)">
                <i class="fas fa-plus-circle"></i> Adicionar certificado
              </sc-dropdown-item>
            </sc-dropdown>
          </sc-table-column>
        </template>
      </sc-table>
    </section>
    <section>
      <sc-add-cnpj-modal
        :isActive="isAddCnpjModalActive"
        :warehouse_config="currentWarehouseSelected"
        :closeCallback="() => handleCloseModal('isAddCnpjModalActive')"
        :continueCallback="handleAddCnpj"
      />

      <sc-add-certificate-modal
        :isActive="isAddCertificateModalActive"
        :isLoading="currentModalLoading === 'addCertificate'"
        :closeCallback="() => handleCloseModal('isAddCertificateModalActive')"
        :finishCallback="handleAddCertificate"
      />

      <sc-exclude-certificate-modal
        :isActive="isExcludeModalActive"
        :closeCallback="() => handleCloseModal('isExcludeModalActive')"
        :excludeCallback="handleExcludeCertificate"
      />
    </section>
  </sc-page-base>
</template>
<script>
  import { createNamespacedHelpers } from 'vuex';
  import {
    ScPageBase,
    ScTable,
    ScTableColumn,
    ScTag,
    ScDropdown,
    ScDropdownItem,
  } from '@intelipost-js/secure-components';

  import ScAddCnpjModal from './components/sc-add-cnpj-modal/ScAddCnpjModal';
  import ScAddCertificateModal from './components/sc-add-certificate-modal/ScAddCertificateModal';
  import ScExcludeCertificateModal from './components/sc-exclude-certificate-modal/ScExcludeCertificateModal';
  import UTCDateMixin from '../../utils/UTCDateMixin';

  const { mapState, mapActions } = createNamespacedHelpers('recon/configurationSefaz');

  export default {
    name: 'page-configuration-sefaz',
    mixins: [UTCDateMixin],
    data() {
      return {
        perPage: 10,

        isAddCnpjModalActive: false,
        isAddCertificateModalActive: false,
        isExcludeModalActive: false,

        currentWarehouseSelected: {},
      };
    },
    methods: {
      ...mapActions(['getWarehouseConfigList', 'uploadCertificateFile', 'removeCertificateFile']),
      getActionMenuPosition(index) {
        const tableLength = Math.min(this.warehouses.length, this.perPage);
        const isFirst = index === 0;
        const isLastTwo = !isFirst && tableLength - (index + 1) < 2;

        // table overflows with scroll when dropdown is opened
        // on the last row, and the user has to scroll down to click
        // TODO try to solve this with absolute positioning
        if (isLastTwo) return 'is-top-left';
        return 'is-bottom-left';
      },
      openAddCertificateModal(warehouse_config, isUpdate = false) {
        this.currentWarehouseSelected = {
          ...warehouse_config,
          ...(isUpdate && { updateId: warehouse_config.configuration_id }),
        };

        if (!warehouse_config.warehouse_federal_tax_id) {
          this.isAddCnpjModalActive = true;
        } else {
          this.isAddCertificateModalActive = true;
        }
      },
      openExcludeCertificateModal(warehouse_config) {
        this.currentWarehouseSelected = warehouse_config;
        this.isExcludeModalActive = true;
      },
      handleCloseModal(modalFlagName) {
        this[modalFlagName] = false;
        this.currentWarehouseSelected = {};
      },
      handleAddCnpj(newCnpj) {
        // close the first modal and open the next
        this.isAddCnpjModalActive = false;
        this.isAddCertificateModalActive = true;

        this.currentWarehouseSelected = {
          ...this.currentWarehouseSelected,
          warehouse_federal_tax_id: newCnpj,
        };
      },
      handleAddCertificate(addCertificateForm) {
        const uploadForm = { ...this.currentWarehouseSelected, ...addCertificateForm };
        this.uploadCertificateFile(uploadForm).then(() => {
          this.handleCloseModal('isAddCertificateModalActive');
          this.getWarehouseConfigList();
        });
      },
      handleExcludeCertificate() {
        const { configuration_id } = this.currentWarehouseSelected;
        this.removeCertificateFile(configuration_id).then(() => {
          this.handleCloseModal('isExcludeModalActive');
          this.getWarehouseConfigList();
        });
      },
    },
    computed: {
      ...mapState(['loadingWarehouses', 'currentModalLoading', 'warehouses']),
      isPaginated() {
        return this.warehouses.length >= this.perPage;
      },
    },
    beforeMount() {
      this.getWarehouseConfigList();
    },
    components: {
      ScPageBase,
      ScTable,
      ScTableColumn,
      ScTag,
      ScDropdown,
      ScDropdownItem,
      ScAddCnpjModal,
      ScAddCertificateModal,
      ScExcludeCertificateModal,
    },
  };
</script>
<style lang="scss">
  @import "~@intelipost-js/secure-ui/src/scss/variables"; //eslint-disable-line
  .configured-icon {
    color: green;
    &:hover {
      cursor: pointer;
    }
  }
</style>



// WEBPACK FOOTER //
// node_modules/@intelipost-modules/secure-recon/features/configuration-sefaz/PageConfigurationSefaz.vue?69a10a07